import isEmpty from "is-empty";
import { useState } from "react";
import toast from "react-hot-toast";
import {
  loginMutation,
  updateCustomerAccountMutation,
} from "rsl-api-106/lib/mutations";
import {
  Mutation,
  MutationLoginArgs,
  MutationUpdateCustomerAccountArgs,
} from "rsl-api-106/lib/types";

import { useApolloClient } from "@apollo/client";
import Button from "@components/_Layout/Controls/Button/Button";
import InputPassword from "@components/_Layout/Controls/InputPassword/InputPassword";
import InputText from "@components/_Layout/Controls/InputText";
import Popup from "@components/_Layout/Controls/Popup/Popup";
import LoadingScreen from "@components/_Layout/Loaders/LoadingScreen/LoadingScreen";
import { useAuth, useSession } from "@hooks";
import { ReservationPage } from "@pages";

export const ReservationPageWrapper = () => {
  /* Hooks */
  const client = useApolloClient();
  const { getLanguageVarValue, sessionId, authMode } = useSession();
  const { user, loading, setUser } = useAuth();

  const [pin, setPin] = useState<string>(""); //7152
  const [code, setCode] = useState<string>(""); //4000001Y
  const [email, setEmail] = useState<string>("");

  const [loadingLogin, setLoadingLogin] = useState<boolean>(false);
  const [loadingUpdate, setLoadingUpdate] = useState<boolean>(false);

  /* Functions */
  const handleSubmitLogin = (e: React.FormEvent) => {
    e.preventDefault();
    setLoadingLogin(true);
    client
      .mutate<Mutation, MutationLoginArgs>({
        mutation: loginMutation(),
        variables: {
          session: { name: sessionId },
          input: {
            password: pin,
            login: code,
          },
        },
        fetchPolicy: "no-cache",
      })
      .then((response) => {
        if (
          response.data &&
          response.data.login &&
          response.data.login.service
        ) {
          setUser(response.data.login.service);
        }
      })
      .catch((err) => {
        toast.error(err.message);
      })
      .finally(() => {
        setLoadingLogin(false);
      });
  };

  const handleSubmitChangeEmail = (e: React.FormEvent) => {
    e.preventDefault();
    setLoadingUpdate(true);
    client
      .mutate<Mutation, MutationUpdateCustomerAccountArgs>({
        mutation: updateCustomerAccountMutation(),
        variables: {
          session: { name: sessionId },
          input: {
            birthDate: user?.birthDate,
            firstName: user?.firstName ?? "",
            lastName: user?.lastName ?? "",
            address: {
              mail: email,
            },
          },
        },
        fetchPolicy: "no-cache",
      })
      .then((response) => {
        if (
          response.data &&
          response.data.updateCustomerAccount &&
          response.data.updateCustomerAccount.service
        ) {
          setUser(response.data.updateCustomerAccount.service);
        }
      })
      .catch((err) => {
        toast.error(err.message);
      })
      .finally(() => {
        setLoadingUpdate(false);
      });
  };

  /* Render */
  if (authMode === 1 && user === null && !loading)
    return (
      <Popup
        title={getLanguageVarValue("LANG_neobooking_to_log_in")}
        enableClose={false}
        open={true}
        btnElement={() => <></>}
      >
        <form
          onSubmit={handleSubmitLogin}
          className="tw-flex tw-flex-col tw-gap-4 "
        >
          <div className="tw-grid tw-grid-cols-2 tw-gap-6 tw-my-4">
            <div className="tw-col-span-2">
              <InputText
                required
                aria-required="true"
                value={code}
                additionalClassname="tw-w-full"
                onChange={(e) => setCode(e.target.value)}
                placeholder={getLanguageVarValue("LANG_neobooking_identifiant")}
              />
            </div>
            <div className="tw-col-span-2">
              <InputPassword
                value={pin}
                onChange={(e) => setPin(e.target.value)}
                required
                aria-required="true"
                additionalClassname="tw-w-full"
                placeholder={getLanguageVarValue("LANG_neobooking_codepin")}
              />
            </div>
          </div>
          <div className="tw-flex tw-justify-end">
            <Button
              isLoading={loadingLogin}
              disabled={loadingLogin}
              type="submit"
              variant="gradient"
            >
              {getLanguageVarValue("LANG_neobooking_to_log_in")}
            </Button>
          </div>
        </form>
      </Popup>
    );

  if (loading) {
    return <LoadingScreen />;
  }

  if (
    authMode === 1 &&
    user !== null &&
    isEmpty(user.address?.mail) &&
    !loading
  ) {
    return (
      <Popup
        title={getLanguageVarValue("LANG_neobooking_popup_cgos_email_title")}
        enableClose={false}
        open={true}
        btnElement={() => <></>}
      >
        <form
          onSubmit={handleSubmitChangeEmail}
          className="tw-flex tw-flex-col tw-gap-4 "
        >
          <div className="tw-grid tw-grid-cols-2 tw-gap-6 tw-my-4">
            <div className="tw-col-span-2">
              <p className="tw-mb-4">
                {getLanguageVarValue(
                  "LANG_neobooking_popup_cgos_email_subtitle"
                )}
              </p>
              <InputText
                required
                type="email"
                aria-required="true"
                value={email}
                additionalClassname="tw-w-full"
                onChange={(e) => setEmail(e.target.value)}
                placeholder={`${getLanguageVarValue("LANG_neobooking_email")}*`}
              />
            </div>
          </div>
          <div className="tw-flex tw-justify-end">
            <Button
              isLoading={loadingUpdate}
              disabled={loadingUpdate}
              type="submit"
              variant="gradient"
            >
              {getLanguageVarValue("LANG_neobooking_edit")}
            </Button>
          </div>
        </form>
      </Popup>
    );
  }

  return <ReservationPage />;
};
